import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";

export interface ArticleNotesHandle {
  expand: () => void;
  highlight: (footnoteId: string) => void;
}

interface ArticleNotesProps {
  children?: ReactNode;
}

function countNoteItems(children: ReactNode): number {
  let count = 0;
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    if (child.type === "ol") {
      Children.forEach((child.props as { children?: ReactNode }).children, (li) => {
        if (isValidElement(li) && li.type === "li") count++;
      });
      return;
    }
    count += countNoteItems((child.props as { children?: ReactNode }).children);
  });
  return count;
}

/** Drop the stock GFM label heading (h2#footnote-label). */
function withoutLabelHeading(children: ReactNode): ReactNode {
  return Children.toArray(children).filter((child) => {
    if (!isValidElement(child)) return true;
    if (child.type !== "h2" && child.type !== "h3") return true;
    const props = child.props as { id?: string };
    return props.id !== "footnote-label";
  });
}

function withHighlightedItems(children: ReactNode, highlightedId: string | null): ReactNode {
  return Children.map(children, (child) => {
    if (!isValidElement(child)) return child;
    const props = child.props as {
      children?: ReactNode;
      id?: string;
      className?: string;
    };

    if (child.type === "li") {
      const isHit = highlightedId != null && props.id === highlightedId;
      const className = [props.className, isHit ? "is-highlighted" : null]
        .filter(Boolean)
        .join(" ");
      return cloneElement(child as ReactElement<{ className?: string }>, {
        className: className || undefined,
      });
    }

    if (props.children == null) return child;
    return cloneElement(child as ReactElement<{ children?: ReactNode }>, {
      children: withHighlightedItems(props.children, highlightedId),
    });
  });
}

export const ArticleNotes = forwardRef<ArticleNotesHandle, ArticleNotesProps>(function ArticleNotes(
  { children },
  ref,
) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const rootRef = useRef<HTMLElement>(null);
  const highlightTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingScrollId = useRef<string | null>(null);

  const noteCount = countNoteItems(children);
  const body = withHighlightedItems(withoutLabelHeading(children), highlightedId);

  const expand = useCallback(() => {
    setIsExpanded(true);
  }, []);

  const highlight = useCallback((footnoteId: string) => {
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
    pendingScrollId.current = footnoteId;
    setIsExpanded(true);
    setHighlightedId(footnoteId);
    highlightTimer.current = setTimeout(() => {
      setHighlightedId(null);
    }, 1600);
  }, []);

  useImperativeHandle(ref, () => ({ expand, highlight }), [expand, highlight]);

  useEffect(() => {
    if (!isExpanded || !pendingScrollId.current) return;
    const id = pendingScrollId.current;
    pendingScrollId.current = null;
    requestAnimationFrame(() => {
      const root = rootRef.current;
      const target =
        root?.querySelector<HTMLElement>(`#${CSS.escape(id)}`) || document.getElementById(id);
      target?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  }, [isExpanded, highlightedId]);

  useEffect(() => {
    return () => {
      if (highlightTimer.current) clearTimeout(highlightTimer.current);
    };
  }, []);

  if (noteCount === 0) return null;

  return (
    <aside
      ref={rootRef}
      className={`article-notes footnotes ${isExpanded ? "is-expanded" : ""}`}
      data-footnotes=""
    >
      <div
        className="footnotes-header"
        onClick={() => setIsExpanded(!isExpanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }
        }}
      >
        <h3 className="footnotes-title">
          <span className="footnotes-icon">&#128209;</span>
          Notes ({noteCount})
        </h3>
        <button
          className="footnotes-toggle"
          type="button"
          aria-label={isExpanded ? "Masquer les notes" : "Afficher les notes"}
          aria-expanded={isExpanded}
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
        >
          {isExpanded ? "Masquer" : "Afficher"}
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
      </div>

      <div className="footnotes-body">
        <div className="footnotes-body-inner article-notes-body">{body}</div>
      </div>
    </aside>
  );
});
